import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getAccessToken } from '../lib/api.js';
import { LeadStatusBadge, LeadStatusSelect } from '../components/LeadStatusBadge.js';

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  sourcePage: string;
  crmTags: string[] | null;
  status: string;
  createdAt: string;
  notifications?: { email: string; sms: string; voice: string };
}

function NotifIcon({ channel, status }: { channel: string; status: string }) {
  if (status === 'none') return null;

  const colors = {
    sent: 'text-green-500',
    pending: 'text-yellow-500',
    queued: 'text-yellow-500',
    failed: 'text-red-500',
    skipped: 'text-gray-300',
  };

  const icons: Record<string, string> = {
    email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    sms: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    voice: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  };

  const color = colors[status as keyof typeof colors] ?? 'text-gray-300';

  return (
    <svg className={`w-3.5 h-3.5 ${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" title={`${channel}: ${status}`}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[channel] ?? ''} />
    </svg>
  );
}

interface LeadListResponse {
  leads: Lead[];
  next_cursor: string | null;
  total_count: number;
}

const DATE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 Days' },
  { key: '30d', label: '30 Days' },
] as const;

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function getAvatarColor(name: string): string {
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500',
    'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-rose-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length]!;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  const days = Math.floor(seconds / 86400);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function LeadCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gray-200 rounded-full" />
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
          <div className="h-3 bg-gray-100 rounded w-48" />
        </div>
        <div className="h-3 bg-gray-100 rounded w-12" />
      </div>
    </div>
  );
}

export function LeadFeedPage() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [locations, setLocations] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
        if (payload.org_id) {
          apiFetch<{ locations: Array<{ id: string; name: string }> }>(`/orgs/${payload.org_id}`)
            .then(data => setLocations(data.locations))
            .catch(() => {});
        }
      } catch { /* */ }
    }
  }, []);

  const fetchLeads = useCallback(async (nextCursor?: string | null, refresh = false) => {
    if (refresh) setIsRefreshing(true); else setIsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '25' });
      if (nextCursor) params.set('cursor', nextCursor);
      if (search.trim()) params.set('search', search.trim());
      if (selectedLocationId) params.set('location_id', selectedLocationId);
      if (dateFilter === '7d') params.set('date_from', new Date(Date.now() - 7 * 86400000).toISOString());
      else if (dateFilter === '30d') params.set('date_from', new Date(Date.now() - 30 * 86400000).toISOString());
      else if (dateFilter === 'today') {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        params.set('date_from', today.toISOString());
      }

      const data = await apiFetch<LeadListResponse>(`/leads?${params}`);
      if (nextCursor) setLeads(prev => [...prev, ...data.leads]);
      else setLeads(data.leads);
      setCursor(data.next_cursor);
      setTotalCount(data.total_count);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [dateFilter, search, selectedLocationId]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleExport = async () => {
    try {
      const res = await fetch('/v1/leads/export', {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      await apiFetch(`/leads/${leadId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)),
      );
    } catch (err) {
      console.error('Failed to update lead status:', err);
    }
  };

  // Detail view
  if (selectedLeadId) {
    const lead = leads.find(l => l.id === selectedLeadId);
    if (lead) {
      return (
        <LeadDetailView
          lead={lead}
          onBack={() => setSelectedLeadId(null)}
          onStatusChange={(newStatus) => handleStatusChange(lead.id, newStatus)}
        />
      );
    }
  }

  return (
    <div className="space-y-5">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Leads</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
          <p className="text-2xl font-bold text-primary-600">
            {leads.filter(l => {
              const d = new Date(l.createdAt);
              const today = new Date(); today.setHours(0,0,0,0);
              return d >= today;
            }).length}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">Today</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
          <p className="text-2xl font-bold text-green-600">
            {leads.filter(l => {
              const d = new Date(l.createdAt);
              return d >= new Date(Date.now() - 7 * 86400000);
            }).length}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">This Week</p>
        </div>
      </div>

      {/* Location picker */}
      {locations.length > 1 && (
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedLocationId(null)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              !selectedLocationId ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >All Locations</button>
          {locations.map(loc => (
            <button key={loc.id}
              onClick={() => setSelectedLocationId(loc.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                selectedLocationId === loc.id ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >{loc.name}</button>
          ))}
        </div>
      )}

      {/* Search + actions */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => fetchLeads(null, true)}
          disabled={isRefreshing}
          className="p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          title="Refresh"
        >
          <svg className={`w-4 h-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
        <button
          onClick={handleExport}
          className="p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          title="Export CSV"
        >
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </button>
      </div>

      {/* Date filter pills */}
      <div className="flex gap-2">
        {DATE_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setDateFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              dateFilter === f.key
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Lead list */}
      {isLoading && leads.length === 0 ? (
        <div className="space-y-3">
          <LeadCardSkeleton />
          <LeadCardSkeleton />
          <LeadCardSkeleton />
          <LeadCardSkeleton />
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="text-gray-900 font-medium">No leads yet</h3>
          <p className="text-gray-500 text-sm mt-1">New leads will appear here as they come in.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map(lead => (
            <button
              key={lead.id}
              onClick={() => setSelectedLeadId(lead.id)}
              className="w-full bg-white rounded-xl border border-gray-100 p-4 text-left hover:border-primary-200 hover:shadow-sm transition-all active:scale-[0.99]"
            >
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className={`w-10 h-10 ${getAvatarColor(lead.name)} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white text-xs font-bold">{getInitials(lead.name)}</span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900 text-sm truncate">{lead.name}</p>
                    <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{timeAgo(lead.createdAt)}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{lead.email}{lead.phone ? ` · ${lead.phone}` : ''}</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <LeadStatusBadge status={lead.status} />
                    <span className="text-xs text-gray-400 truncate">{lead.sourcePage}</span>
                    {lead.crmTags?.slice(0, 2).map(tag => (
                      <span key={tag} className="text-xs bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  {/* Notification status icons */}
                  {lead.notifications && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <NotifIcon channel="email" status={lead.notifications.email} />
                      <NotifIcon channel="sms" status={lead.notifications.sms} />
                      <NotifIcon channel="voice" status={lead.notifications.voice} />
                    </div>
                  )}
                </div>

                {/* Chevron */}
                <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}

          {cursor && (
            <button
              onClick={() => fetchLeads(cursor)}
              disabled={isLoading}
              className="w-full py-3 text-sm text-primary-600 hover:text-primary-800 disabled:opacity-50 font-medium"
            >
              {isLoading ? 'Loading...' : 'Load more leads'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== LEAD DETAIL VIEW ====================

function LeadDetailView({ lead, onBack, onStatusChange }: { lead: Lead; onBack: () => void; onStatusChange: (status: string) => void }) {
  return (
    <div className="space-y-4">
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Lead header */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-14 h-14 ${getAvatarColor(lead.name)} rounded-full flex items-center justify-center`}>
            <span className="text-white text-lg font-bold">{getInitials(lead.name)}</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{lead.name}</h2>
            <p className="text-sm text-gray-500">
              {new Date(lead.createdAt).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <a href={`mailto:${lead.email}`} className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email
          </a>
          {lead.phone && (
            <>
              <a href={`tel:${lead.phone}`} className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call
              </a>
              <a href={`sms:${lead.phone}`} className="flex-1 flex items-center justify-center gap-2 bg-gray-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Text
              </a>
            </>
          )}
        </div>
      </div>

      {/* Details sections */}
      <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-100">
        <DetailRow label="Email" value={lead.email} href={`mailto:${lead.email}`} />
        {lead.phone && <DetailRow label="Phone" value={lead.phone} href={`tel:${lead.phone}`} />}
        <DetailRow label="Source Page" value={lead.sourcePage} />
        <DetailRow label="Status" value={
          <LeadStatusSelect status={lead.status} onChange={onStatusChange} />
        } />
        {lead.crmTags && lead.crmTags.length > 0 && (
          <DetailRow label="Tags" value={
            <div className="flex flex-wrap gap-1">
              {lead.crmTags.map(tag => (
                <span key={tag} className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded font-medium">{tag}</span>
              ))}
            </div>
          } />
        )}
      </div>

      <ActivityTimeline leadId={lead.id} />
    </div>
  );
}

function ActivityTimeline({ leadId }: { leadId: string }) {
  const [activity, setActivity] = useState<Array<{
    type: string;
    channel?: string;
    status: string;
    error?: string | null;
    timestamp: string;
    detail?: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ activity: typeof activity }>(`/leads/${leadId}/activity`)
      .then(data => setActivity(data.activity))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [leadId]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-8 bg-gray-100 rounded" />)}
        </div>
      </div>
    );
  }

  if (activity.length === 0) return null;

  const statusColors: Record<string, string> = {
    sent: 'bg-green-500',
    delivered: 'bg-green-500',
    pending: 'bg-yellow-500',
    queued: 'bg-yellow-500',
    failed: 'bg-red-500',
    skipped: 'bg-gray-400',
  };

  const statusLabels: Record<string, string> = {
    sent: 'Sent',
    delivered: 'Delivered',
    pending: 'Pending',
    queued: 'Queued (quiet hours)',
    failed: 'Failed',
    skipped: 'Skipped',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <h3 className="text-sm font-medium text-gray-700 mb-4">Activity</h3>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200" />

        <div className="space-y-4">
          {activity.map((event, i) => (
            <div key={i} className="flex gap-3 relative">
              {/* Dot */}
              <div className={`w-3.5 h-3.5 rounded-full ${statusColors[event.status] ?? 'bg-gray-300'} flex-shrink-0 mt-0.5 ring-2 ring-white`} />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-900">
                    {event.detail ?? event.type}
                    {event.channel && <span className="text-gray-400"> ({event.channel})</span>}
                  </p>
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                    event.status === 'sent' || event.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    event.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {statusLabels[event.status] ?? event.status}
                  </span>
                </div>
                {event.error && <p className="text-xs text-red-500 mt-0.5">{event.error}</p>}
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(event.timestamp).toLocaleString('en-US', {
                    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, href }: { label: string; value: React.ReactNode; href?: string }) {
  const content = href ? (
    <a href={href} className="text-primary-600 hover:underline">{value}</a>
  ) : (
    <span className="text-gray-900">{value}</span>
  );

  return (
    <div className="px-4 py-3 flex items-center justify-between">
      <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</span>
      <div className="text-sm text-right">{content}</div>
    </div>
  );
}
